import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Wand2, ShieldCheck, ShieldAlert, Search, X } from "lucide-react";
import { CloudIcon, LangIcon } from "@/components/brand-icons";

const DEFAULTS = { name: "", cloud: "all", language: "all", region: "all", minCompliance: 0, maxCompliance: 100 };

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [f, setF] = useState(DEFAULTS);

  useEffect(() => {
    client.get("/projects").then((r) => setProjects(r.data));
  }, []);

  const regions = useMemo(() => Array.from(new Set(projects.map((p) => p.region))).sort(), [projects]);

  const filtered = useMemo(() => {
    const q = f.name.trim().toLowerCase();
    return projects.filter((p) => {
      if (q && !p.name.toLowerCase().includes(q)) return false;
      if (f.cloud !== "all" && p.cloud !== f.cloud) return false;
      if (f.language !== "all" && p.language !== f.language) return false;
      if (f.region !== "all" && p.region !== f.region) return false;
      const score = p.compliance?.score ?? 0;
      if (score < f.minCompliance || score > f.maxCompliance) return false;
      return true;
    });
  }, [projects, f]);

  const activeFilters = [
    f.name.trim() && { key: "name", label: `name·${f.name.trim()}` },
    f.cloud !== "all" && { key: "cloud", label: `cloud·${f.cloud}` },
    f.language !== "all" && { key: "language", label: `iac·${f.language}` },
    f.region !== "all" && { key: "region", label: `region·${f.region}` },
    (f.minCompliance > 0 || f.maxCompliance < 100) && { key: "compliance", label: `compliance·${f.minCompliance}-${f.maxCompliance}%` },
  ].filter(Boolean);

  return (
    <div className="p-10">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono-tight text-xs uppercase tracking-widest text-slate-500">// projects</p>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight" data-testid="page-title">Your IaC projects</h1>
          <p className="mt-1 text-sm text-slate-600">All scaffolds ever generated in this workspace.</p>
        </div>
        <Link to="/app/generate" data-testid="projects-new">
          <Button className="h-10 rounded-md bg-slate-900 text-white hover:bg-slate-800">
            <Wand2 className="mr-2 h-4 w-4" /> New project
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="mb-4 rounded-md border border-slate-200 bg-white p-4" data-testid="filters-panel">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1.5fr)_repeat(3,minmax(0,1fr))_minmax(0,1.5fr)]">
          <div>
            <label className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">Search name</label>
            <div className="relative mt-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="filter-name"
                value={f.name}
                onChange={(e) => setF({ ...f, name: e.target.value })}
                placeholder="cor-…"
                className="h-9 border-slate-200 pl-8 font-mono-tight text-sm"
              />
            </div>
          </div>
          <div>
            <label className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">Cloud</label>
            <Select value={f.cloud} onValueChange={(v) => setF({ ...f, cloud: v })}>
              <SelectTrigger data-testid="filter-cloud" className="mt-1 h-9 border-slate-200 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All clouds</SelectItem>
                <SelectItem value="azure"><span className="inline-flex items-center gap-2"><CloudIcon cloud="azure" className="h-3.5 w-3.5" /> Azure</span></SelectItem>
                <SelectItem value="aws"><span className="inline-flex items-center gap-2"><CloudIcon cloud="aws" className="h-3.5 w-3.5" /> AWS</span></SelectItem>
                <SelectItem value="gcp"><span className="inline-flex items-center gap-2"><CloudIcon cloud="gcp" className="h-3.5 w-3.5" /> GCP</span></SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">IaC</label>
            <Select value={f.language} onValueChange={(v) => setF({ ...f, language: v })}>
              <SelectTrigger data-testid="filter-language" className="mt-1 h-9 border-slate-200 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All IaC</SelectItem>
                <SelectItem value="terraform"><span className="inline-flex items-center gap-2"><LangIcon language="terraform" className="h-3.5 w-3.5" /> Terraform</span></SelectItem>
                <SelectItem value="bicep"><span className="inline-flex items-center gap-2"><LangIcon language="bicep" className="h-3.5 w-3.5" /> Bicep</span></SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">Region</label>
            <Select value={f.region} onValueChange={(v) => setF({ ...f, region: v })}>
              <SelectTrigger data-testid="filter-region" className="mt-1 h-9 border-slate-200 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All regions</SelectItem>
                {regions.map((r) => (
                  <SelectItem key={r} value={r}><span className="font-mono-tight text-xs">{r}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="flex items-center justify-between font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">
              <span>Compliance</span>
              <span className="text-slate-700" data-testid="filter-compliance-value">{f.minCompliance}–{f.maxCompliance}%</span>
            </label>
            <div className="mt-3 px-1">
              <Slider
                data-testid="filter-compliance"
                min={0}
                max={100}
                step={5}
                value={[f.minCompliance, f.maxCompliance]}
                onValueChange={([lo, hi]) => setF({ ...f, minCompliance: lo, maxCompliance: hi })}
              />
            </div>
          </div>
        </div>
        {activeFilters.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3" data-testid="active-filters">
            <span className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">// active</span>
            {activeFilters.map((af) => (
              <Badge key={af.key} className="rounded-full bg-slate-900 text-[10px] uppercase tracking-widest text-white">
                {af.label}
              </Badge>
            ))}
            <button
              type="button"
              onClick={() => setF(DEFAULTS)}
              data-testid="filter-clear"
              className="ml-auto inline-flex items-center gap-1 font-mono-tight text-[11px] text-slate-500 hover:text-slate-900"
            >
              <X className="h-3 w-3" /> clear all
            </button>
          </div>
        )}
      </div>

      <div className="mb-3 font-mono-tight text-xs text-slate-500" data-testid="filter-count">
        {filtered.length} / {projects.length} projects
      </div>

      <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="projects-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-6 py-3 font-medium">Name</th>
              <th className="px-6 py-3 font-medium">Cloud</th>
              <th className="px-6 py-3 font-medium">IaC</th>
              <th className="px-6 py-3 font-medium">Region</th>
              <th className="px-6 py-3 font-medium">Resources</th>
              <th className="px-6 py-3 font-medium">Compliance</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`row-${p.name}`}>
                <td className="px-6 py-3 font-medium text-slate-900">
                  <Link to={`/app/projects/${p.id}`} className="hover:underline" data-testid={`project-name-${p.name}`}>{p.name}</Link>
                </td>
                <td className="px-6 py-3">
                  <Badge className="flex w-fit items-center gap-1.5 rounded-full bg-slate-100 text-[10px] uppercase tracking-widest text-slate-600">
                    <CloudIcon cloud={p.cloud} className="h-3 w-3" /> {p.cloud}
                  </Badge>
                </td>
                <td className="px-6 py-3">
                  <span className="inline-flex items-center gap-1.5 font-mono-tight text-xs text-slate-600">
                    <LangIcon language={p.language} className="h-3 w-3" /> {p.language}
                  </span>
                </td>
                <td className="px-6 py-3 font-mono-tight text-xs text-slate-600">{p.region}</td>
                <td className="px-6 py-3 text-slate-600">{p.resources.length}</td>
                <td className="px-6 py-3">
                  <span className={p.compliance?.compliant ? "inline-flex items-center gap-1 text-emerald-600" : "inline-flex items-center gap-1 text-amber-600"}>
                    {p.compliance?.compliant ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldAlert className="h-3.5 w-3.5" />}
                    {p.compliance?.score ?? "—"}%
                  </span>
                </td>
                <td className="px-6 py-3 text-right">
                  <Link to={`/app/projects/${p.id}`} className="text-slate-600 hover:text-slate-900" data-testid={`open-${p.name}`}>Open →</Link>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-10 text-center text-sm text-slate-500" data-testid="empty-state">
                {projects.length === 0 ? "No projects yet." : "No project matches your filters."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
